import { useEffect, useRef, useState } from "react";
import { Button, Container, HeadlineAnim, InducteeCard, RevealWrapper } from "@components";
import { ensurePostsAreInductees, getHighlightMarkup, getLinkData } from "@/core/utils";
import { FeaturedInducteesFragment, GridInducteesFragment } from "@graphqlTypes";
import { GlobalGridInducteesFragment } from "@/fragments/Components/GridInductees";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
import { useInView } from "motion/react";
import { useViewSize } from "@/providers/ViewSizeContext";

import { AnimVals } from "@/utils/animUtils";
import { useDebug } from "@/utils/useDebugLog";

import className from "classnames/bind";
import styles from "./GridInductees.module.scss";
const cx = className.bind(styles);

type GridInducteesProps = {
  global?: boolean;
  headline?: string;
  colorOverride?: string[];
  cta?: Omit<GridInducteesFragment['cta'], '__typename'>;
  inductees?: FeaturedInducteesFragment['nodes'];
};

const getRandomInductees = (inductees: PageWrapperData['siteSettings']['featuredInductees']['nodes']) => {
  const filteredInductees = ensurePostsAreInductees({ nodes: inductees });

  const count = filteredInductees.length >= 12 ? 12 : 8;
  const shuffled = [...filteredInductees].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
};

export default function GridInductees(props: GridInducteesProps) {
  const { log } = useDebug({ name: 'GridInductees', bgColor: 'cyan', enabled: false });
  const { featuredInductees } = usePageWrapperData(); 

  const { headline, cta, colorOverride, inductees } = props;
  const highlightColor = colorOverride?.[0] || "";

  const headlineMarkup = getHighlightMarkup(headline);
  const ctaData = getLinkData(cta);
  const [randomInductees, setRandomInductees] = useState<ReturnType<typeof getRandomInductees>>([]);
  const [style, setStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    setRandomInductees(getRandomInductees(inductees || featuredInductees.nodes));
  }, [featuredInductees]);

  useEffect(() => {
    setStyle({ "--highlight-color": highlightColor } as React.CSSProperties);
  }, [highlightColor]);

  // -------------------- Animation

  const rootEl = useRef<HTMLDivElement>(null);
  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });
  const isRevealEnabled = true;

  // -------------------- Render

  return (
    <section className={cx(["grid-inductees"])} style={style} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        <Container className={cx(["container"])}>

          <HeadlineAnim
            tag="h2"
            text={headlineMarkup}
            className={cx(["title-uc-l"])}
            isRevealEnabled={isRevealEnabled}
            isInView={isInView}
          />

          <div className={cx("grid")}>
            {randomInductees.map((inductee, index) => (
              <div key={index} className={cx("inductee-item")}>
                <InducteeCard inductee={inductee} />
              </div>
            ))}
          </div>

          { ctaData &&
            <div className={cx("cta")}>
              <Button {...ctaData} />
            </div>
          }

        </Container>
      </RevealWrapper>
    </section>
  );

}

GridInductees.fragments = {
  entry: GlobalGridInducteesFragment,
};
